<template>
  <div class="point-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="现有几何关系" name="relations">
        <div class="existing-relations-section">
          <SelectionListFrame
              :items="existingRelations"
              selection-behavior="ctrl-multiple"
              list-role="list"
              :visible-rows="4"
              context-menu-enabled
              :is-item-delete-enabled="isPointRelationItemDeleteEnabled"
              :show-delete-all-action="hasDeletablePointRelations"
              item-key-field="relationId"
              @delete-item="handleDeleteRelationSelection"
              @delete-all="handleDeleteAllRelations"
          >
            <template #item="{ item }">
              <div class="relation-main">
                <n-text>{{ resolveRelationLabel(item) }}</n-text>
                <n-text
                    v-if="resolveRelatedEntityLabels(item).length > 0"
                    depth="3"
                    class="relation-related"
                >
                  {{ resolveRelatedEntityLabels(item).join('、') }}
                </n-text>
              </div>
            </template>
          </SelectionListFrame>
        </div>
      </n-collapse-item>
      <n-collapse-item title="添加几何关系" name="add-relations">
        <div class="relation-button-list">
          <n-button
              v-for="relation in availableRelations"
              :key="relation.type"
              size="small"
              :disabled="relation.disabled || !canManageRelations"
              :title="relation.reason"
              @click="handleAddRelation(relation.type)"
          >
            {{ relation.label }}
          </n-button>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <n-text v-if="isFixedPoint" depth="3" class="fixed-tip">当前点已固定，参数不可编辑。</n-text>
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
                :value="point2d.x"
                size="small"
                :disabled="!canEditParameters"
                :show-button="true"
                :step="1"
                @update:value="(value) => handlePointInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
                :value="point2d.y"
                size="small"
                :disabled="!canEditParameters"
                :show-button="true"
                :step="1"
                @update:value="(value) => handlePointInput('y', value)"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {NButton, NCollapse, NCollapseItem, NInputNumber, NText,} from 'naive-ui';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import { useGetSelectPoint, useViewer } from '@/hooks';
import type { PropertyManagerContext } from '@/core';
import {
  type SketchRelationSnapshot,
  SketchRelationKind as CadSketchRelationKind,
  convertMmToM,
  convertMToMm,
  roundToEightDecimalPlaces,
} from '@puppycad/cad-core';
import type { Subscription } from 'rxjs';

defineOptions({
  inheritAttrs: false,
});

interface PointPropertySectionProps {
  /** 属性面板上下文，用于统一提交草图属性编辑。 */
  context?: PropertyManagerContext;
}

const props = defineProps<PointPropertySectionProps>();

/**
 * @description 点关系列表项视图模型。
 */
interface PointRelationItemViewModel {
  /** 关系 ID。 */
  relationId: number;
  /** 关系名称。 */
  label: string;
  /** 关联实体标签。 */
  relatedEntityLabels: string[];
  /** 是否允许删除。 */
  deletable: boolean;
}

/**
 * @description 点关系按钮视图模型。
 */
interface PointRelationButtonViewModel {
  /** 关系类型。 */
  type: CadSketchRelationKind;
  /** 按钮文案。 */
  label: string;
  /** 是否禁用。 */
  disabled: boolean;
  /** 禁用原因。 */
  reason?: string;
}

const {
  getSelectedPoint,
  onPointChanged,
  onPointParametersChanged,
} = useGetSelectPoint();
type SelectedPoint = any;
const { getViewer } = useViewer();
let pointChangedSubscription: Subscription | null = null;
/** 绘制中实时参数订阅。 */
let pointParamsSubscription: Subscription | null = null;

/**
 * @description 当前选中点对象。
 */
const selectedPointRef = ref<SelectedPoint>(null);

onMounted(() => {
  const point = getSelectedPoint();
  sync(point);
  pointChangedSubscription = onPointChanged().subscribe((nextPoint) => {
    sync(nextPoint);
  });

  
  pointParamsSubscription = onPointParametersChanged().subscribe((snapshot) => {
    
    // 从底层拿到的坐标（米）转换为毫米展示在输入框中
    if (snapshot.x !== null) {
      point2d.value.x = roundToEightDecimalPlaces(convertMToMm(snapshot.x));
    }
    if (snapshot.y !== null) {
      point2d.value.y = roundToEightDecimalPlaces(convertMToMm(snapshot.y));
    }
    
  });
  
});

onBeforeUnmount(() => {
  if (pointChangedSubscription) {
    pointChangedSubscription.unsubscribe();
    pointChangedSubscription = null;
  }
  if (pointParamsSubscription) {
    pointParamsSubscription.unsubscribe();
    pointParamsSubscription = null;
  }
});

const isFixedPoint = computed(() => Boolean(selectedPointRef.value?.isFixed()));
/**
 * @description 当前点现有关系。
 */
const existingRelations = computed<PointRelationItemViewModel[]>(() => {
  const point = selectedPointRef.value;
  if (!point) {
    return [];
  }
  return point.constraints.getRelationSnapshots().map((snapshot: SketchRelationSnapshot) => ({
    relationId: snapshot.id,
    label: `${snapshot.name ?? String(snapshot.relationType)}${snapshot.isSuppressed ? '（抑制）' : ''}`,
    relatedEntityLabels: [],
    deletable: isSketchRelationSnapshotDeleteEnabled(snapshot),
  }));
});
/** 是否至少存在一条可删除的普通点关系。 */
const hasDeletablePointRelations = computed(() => (
  existingRelations.value.some((relation) => relation.deletable)
));
/**
 * @description 当前点可添加关系。
 */
const availableRelations = computed<PointRelationButtonViewModel[]>(() => {
  const point = selectedPointRef.value;
  if (!point) {
    return [];
  }
  const hasFix = point.constraints.hasType(CadSketchRelationKind.Fix);
  return [
    {
      type: CadSketchRelationKind.Fix,
      label: '固定',
      disabled: hasFix,
      reason: hasFix ? '当前点已固定' : undefined,
    },
  ];
});
/**
 * @description 是否允许管理点关系。
 */
const canManageRelations = computed(() => Boolean(selectedPointRef.value));
/**
 * @description 是否允许在点属性面板中编辑参数。
 */
const canEditParameters = computed(() => {
  return Boolean(selectedPointRef.value);
});

const point2d = ref({
  x: 0,
  y: 0,
});

/**
 * @description 同步选中点对象与参数展示。
 * @param point 当前选中点对象。
 */
const sync = (point: SelectedPoint): void => {
  selectedPointRef.value = point;
  if (point) {
    
    // 从底层点对象读取的米级坐标转换为毫米
    point2d.value.x = roundToEightDecimalPlaces(convertMToMm(point.x));
    point2d.value.y = roundToEightDecimalPlaces(convertMToMm(point.y));
    
    return;
  }
  point2d.value.x = 0;
  point2d.value.y = 0;
}

/**
 * @description 将未知项解析为点关系项。
 * @param item 关系项。
 * @returns 关系项视图模型。
 */
function toPointRelationItem(item: unknown): PointRelationItemViewModel | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const candidate = item as Partial<PointRelationItemViewModel>;
  if (typeof candidate.relationId !== 'number' || typeof candidate.label !== 'string') {
    return null;
  }
  return {
    relationId: candidate.relationId,
    label: candidate.label,
    relatedEntityLabels: Array.isArray(candidate.relatedEntityLabels)
      ? candidate.relatedEntityLabels.filter((label): label is string => typeof label === 'string')
      : [],
    deletable: candidate.deletable !== false,
  };
}

/**
 * @description 解析关系标签。
 * @param item 模板项。
 * @returns 标签。
 */
function resolveRelationLabel(item: unknown): string {
  return toPointRelationItem(item)?.label ?? '';
}

/**
 * @description 解析关联实体标签列表。
 * @param item 模板项。
 * @returns 关联实体标签列表。
 */
function resolveRelatedEntityLabels(item: unknown): string[] {
  return toPointRelationItem(item)?.relatedEntityLabels ?? [];
}

/**
 * @description 判断关系是否可删除。
 * @param item 模板项。
 * @returns 是否可删除。
 */
const defaultExpanded = ['relations', 'add-relations', 'parameters'];

/**
 * @description 刷新点关系修改后的场景交互显示。
 */
function refreshPointSelectionVisual(): void {
  getViewer()?.refreshSelection?.();
}

/**
 * @description 将点属性编辑统一收口到 cad-core finalizer。
 * @param point 被编辑的点对象。
 */
function finalizePointPropertyEdit(point: SelectedPoint): void {
  if (!point) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(point, {
    reason: 'property-edit',
    sourceObjectId: point.uuid,
  });
}

function handlePointInput(axis: 'x' | 'y', value: number | null): void {
  if (isFixedPoint.value) {
    return;
  }
  if (value === null || Number.isNaN(value)) {
    return;
  }
  const selectedPoint = getSelectedPoint();

  // 点实体参数编辑直接走点对象方法。
  if (selectedPoint) {
    // 用户输入的毫米值转换为底层所需的米级
    selectedPoint.setCoordinateOnReferencePlane(axis, convertMmToM(value));
    finalizePointPropertyEdit(selectedPoint);
    sync(selectedPoint);
    return;
  }
}

/**
 * @description 添加点关系。
 * @param type 关系类型。
 */
function handleAddRelation(type: CadSketchRelationKind): void {
  const point = selectedPointRef.value;
  const viewer = getViewer();

  if (!point || !viewer) {
    return;
  }
  const created = viewer.sketchRelationCreationService.createRelation({
    objects: [point],
    relationKind: type,
  });
  if (!created) {
    return;
  }
  sync(point);
  refreshPointSelectionVisual();
}

/**
 * @description 删除点关系。
 * @param relationId 关系 ID。
 */
async function handleDeleteRelation(relationId: number): Promise<void> {
  if (!Number.isFinite(relationId) || relationId < 0) {
    return;
  }
  const point = selectedPointRef.value;
  if (!point) {
    return;
  }
  const removed = await props.context?.removeSketchRelation?.(point, relationId) ?? false;
  if (!removed) {
    return;
  }
  getViewer()?.removeObjectConstraintMarker?.(point, relationId);
  sync(point);
  refreshPointSelectionVisual();
}

/**
 * @description 处理关系列表右键删除单项。
 * @param payload 列表删除事件载荷。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const relation = toPointRelationItem(payload.item);
  if (!relation || relation.deletable === false || canManageRelations.value === false) {
    return;
  }
  await handleDeleteRelation(relation.relationId);
}

/**
 * 判断点关系列表项是否允许删除。
 * @param item 关系列表项。
 * @returns 普通关系返回 true，尺寸派生只读视图返回 false。
 */
function isPointRelationItemDeleteEnabled(item: unknown): boolean {
  return toPointRelationItem(item)?.deletable === true;
}

/**
 * @description 删除全部点关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const point = selectedPointRef.value;
  if (!point) {
    return;
  }
  for (const item of existingRelations.value.filter((relation) => relation.deletable)) {
    const removed = await props.context?.removeSketchRelation?.(point, item.relationId) ?? false;
    if (!removed) {
      return;
    }
    getViewer()?.removeObjectConstraintMarker?.(point, item.relationId);
  }
  sync(point);
  refreshPointSelectionVisual();
}

</script>

<style scoped>
.point-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.existing-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ------ 草图点固定关系 开始------ */
.relations-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.point-property :deep(.selection-list-frame) {
  max-height: 140px;
}

.relation-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.relation-related {
  font-size: 12px;
}

.relation-button-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fixed-tip {
  display: block;
  margin-bottom: 8px;
}

/* ------ 草图点固定关系 结束------ */

.param-row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
